import { Button, Stack, Tooltip } from "@mui/material";
import SurveyCustomers from "../containers/SurveyCustomers";
import moment from "moment";
import { useRef } from "react";
import { FilterGroup, FilterType } from "../components/TableFilter";
import TableList from "../components/TableList";
import { filterOnKeys } from "../lib/helper";
import { HeadCell, FormRefEvent } from "../models/base.model";
import { SurveyEvent, SurveyEventSubmit } from "../models/survey.event.model";
import { getSurveyEvents, getSurveys } from "../actions/survey.event.action";
import { useConfig } from "../containers/ConfigContext";
import ToggleTabs from "../components/ToggleTabs";

const headCells: readonly HeadCell[] = [
  {
    id: "id",
    label: "ID",
  },
  {
    id: "survey",
    label: "Survey Name",
    render: (v) => (
      <div>
        {v?.name}
      </div>
    ),
  },
  {
    id: "customers",
    label: "Records",
    render: (_, r) => (
      <ViewCustomerRecords
        key={`records_${r.id}`}
        record={r}
      />
    ),
  },
  {
    id: "createdAt",
    label: "Created At",
    render: (v, record) => (
      <div data-testid={`createdAt`}>
        {v && moment(v).format("YYYY-MM-DD HH:mm:ss")}
      </div>
    ),
  },
  {
    id: "updatedAt",
    label: "Updated At",
    render: (v, record) => (
      <div data-testid={`updatedAt`}>
        {v && moment(v).format("YYYY-MM-DD HH:mm:ss")}
      </div>
    ),
  },
];

function ViewCustomerRecords(props: { record: SurveyEvent }) {
  const { record } = props;
  const formRef = useRef<FormRefEvent<SurveyEventSubmit[]>>(null);
  const { localized } = useConfig();
  return (
    <Button
      variant="contained"
      onClick={() => {
        window.webApp.setTab({
          title: `Survey "${record?.survey?.name}" Event Records`,
          element: (
            <SurveyCustomers
              ref={formRef}
              id={record.id}
              companyId={record.companyId}
            />
          ),
          onCancel: async () => {
            if (!formRef.current) {
              throw new Error(
                "Form creation error, please refresh or contact us!"
              );
            }
            await formRef.current.onSubmit();
          },
          cancelText: "Close",
          show: true,
        });
      }}
      data-testid={"Records"}
    >
      {localized("View")}
    </Button>
  );
}

const filters: FilterGroup<SurveyEvent>[] = [
  {
    title: "Filter By Date",
    items: [
      {
        name: "createdAt",
        type: FilterType.date_range,
      },
      {
        name: "updatedAt",
        type: FilterType.date_range,
      },
    ],
  },
];

export default function SubmitRecordPage() {

  const onSearch = async (value: string, original: SurveyEvent[]) => {
    if (original) {
      return { data: original.filter((a) => filterOnKeys(a, ["survey.name"], value)) };
    } else {
      return { data: [] };
    }
  };

  const onLoad = async () => {
    const [surveys, surveyEvents] = await Promise.all([getSurveys(), getSurveyEvents()]);
    surveyEvents.data = surveyEvents.data.map(event => {
      const survey = surveys.data.find(s => s.id === event.surveyId);
      if (survey) {
        event.survey = survey;
      }
      return event;
    })
    return { data: surveyEvents.data, total: surveyEvents.total };
  };

  return (
    <ToggleTabs>
      <Stack>
        <TableList<SurveyEvent>
          headCells={headCells}
          uid="id"
          onSearch={onSearch}
          searchText="Search by name"
          onLoad={onLoad}
          filters={filters}
          initialSortKey="createdAt"
          initialOrder="desc"
          noAction
        />
      </Stack>
    </ToggleTabs>
  );
}
